import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { COGNITO } from '../../../config';

let pool: CognitoUserPool | null = null;

function userPool(): CognitoUserPool {
  if (!pool) {
    pool = new CognitoUserPool({
      UserPoolId: COGNITO.userPoolId,
      ClientId: COGNITO.clientId,
    });
  }
  return pool;
}

/**
 * Opaque handle for an in-flight NEW_PASSWORD_REQUIRED challenge. Carries the
 * live CognitoUser (already mid-auth) plus the writable attributes to echo back
 * to `completeNewPasswordChallenge`, so the UI can finish first login without
 * re-authenticating.
 */
export interface NewPasswordChallenge {
  readonly user: CognitoUser;
  readonly attributes: Record<string, string>;
}

/** Result of a sign-in attempt: fully signed in, or a forced-reset challenge. */
export type SignInResult =
  | { kind: 'ok'; session: CognitoUserSession }
  | { kind: 'newPasswordRequired'; challenge: NewPasswordChallenge };

/**
 * Sign in with email + password. Resolves to `ok` with a session, or to
 * `newPasswordRequired` with a challenge handle when Cognito demands a permanent
 * password (console-created users on first login). Rejects on bad credentials.
 */
export function signIn(email: string, password: string): Promise<SignInResult> {
  const user = new CognitoUser({ Username: email, Pool: userPool() });
  const details = new AuthenticationDetails({ Username: email, Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: (session) => resolve({ kind: 'ok', session }),
      onFailure: (err) => reject(err),
      // A NEW_PASSWORD_REQUIRED challenge fires for console-created users on
      // first login. Surface it (with a live handle) so the UI can prompt for a
      // permanent password instead of forcing a console round-trip.
      newPasswordRequired: (userAttributes) => {
        // email / email_verified are non-writable; Cognito rejects the call if
        // they're echoed back into completeNewPasswordChallenge.
        const { email_verified, email: _email, ...attributes } = userAttributes ?? {};
        void email_verified;
        void _email;
        resolve({ kind: 'newPasswordRequired', challenge: { user, attributes } });
      },
    });
  });
}

/**
 * Complete a NEW_PASSWORD_REQUIRED challenge with a permanent password. On
 * success the user is fully signed in; resolves with the fresh session.
 */
export function completeNewPassword(
  challenge: NewPasswordChallenge,
  newPassword: string,
): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    challenge.user.completeNewPasswordChallenge(newPassword, challenge.attributes, {
      onSuccess: (session) => resolve(session),
      onFailure: (err) => reject(err),
    });
  });
}

/**
 * Start a forgot-password flow: Cognito emails a verification code. Resolves
 * once the request is accepted. With `preventUserExistenceErrors` on the app
 * client, an unknown email still resolves (no user enumeration), so callers must
 * always advance to the confirm step.
 */
export function forgotPassword(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: userPool() });
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/** Finish a forgot-password flow: set a new password using the emailed code. */
export function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: userPool() });
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/** The current cached user, if any (does not validate the session). */
function currentUser(): CognitoUser | null {
  return userPool().getCurrentUser();
}

/**
 * Return a valid id token (JWT) for the current session, refreshing if needed.
 * Rejects if there is no signed-in user or the session can't be refreshed.
 */
export function getIdToken(): Promise<string> {
  const user = currentUser();
  if (!user) return Promise.reject(new Error('Not signed in'));

  return new Promise((resolve, reject) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        reject(err ?? new Error('Session expired'));
        return;
      }
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

/** True if a signed-in user with a valid session exists. */
export function hasValidSession(): Promise<boolean> {
  return getIdToken().then(
    () => true,
    () => false,
  );
}

export function signOut(): void {
  currentUser()?.signOut();
}

/**
 * Return the current user's email, read from the ID token's `email` claim
 * (not `getUsername()`, which returns the pool's opaque internal Username —
 * a GUID, since the pool uses email only as a sign-in alias).
 */
export function currentUserEmail(): Promise<string | undefined> {
  const user = currentUser();
  if (!user) return Promise.resolve(undefined);

  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(undefined);
        return;
      }
      resolve(session.getIdToken().payload.email as string | undefined);
    });
  });
}
